<template>
  <div class="notification-channels-page">
    <div class="page-header">
      <div class="header-content">
        <h1><i class="fas fa-bell"></i> Notification Channels</h1>
        <p>Manage webhook and Slack channels for failure alerts</p>
      </div>
      <div class="header-actions">
        <button class="btn btn-primary" @click="openAddModal">
          <i class="fas fa-plus"></i> Add Channel
        </button>
      </div>
    </div>

    <div v-if="store.error" class="alert alert-error">
      <i class="fas fa-exclamation-circle"></i> {{ store.error }}
      <button class="btn-close" @click="store.clearError()">×</button>
    </div>

    <div v-if="store.loading && store.channels.length === 0" class="loading-state">
      <i class="fas fa-spinner fa-spin"></i> Loading channels...
    </div>

    <div v-else-if="store.channels.length === 0" class="empty-state">
      <i class="fas fa-bell-slash"></i>
      <p>No notification channels configured.</p>
      <button class="btn btn-primary" @click="openAddModal">Add your first channel</button>
    </div>

    <div v-else class="channels-table-wrapper" data-scroll-x>
      <table class="channels-table">
        <thead>
          <tr>
            <th>Label</th>
            <th>Type</th>
            <th>Status</th>
            <th>Last Delivery</th>
            <th>Last Delivery At</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="channel in store.channels" :key="channel.id">
            <td>{{ channel.label }}</td>
            <td>
              <span class="badge badge-type">{{ formatType(channel.channelType) }}</span>
            </td>
            <td>
              <span :class="['badge', channel.enabled ? 'badge-enabled' : 'badge-disabled']">
                {{ channel.enabled ? 'Enabled' : 'Disabled' }}
              </span>
            </td>
            <td>
              <span v-if="channel.lastDeliveryStatus" :class="['badge', 'badge-delivery-' + channel.lastDeliveryStatus]">
                {{ channel.lastDeliveryStatus }}
              </span>
              <span v-else class="text-muted">Never</span>
            </td>
            <td>{{ channel.lastDeliveryAt ? formatDate(channel.lastDeliveryAt) : '—' }}</td>
            <td class="actions-cell">
              <button class="btn btn-sm btn-info" @click="sendTest(channel)" :disabled="testingId === channel.id" title="Send test notification">
                <i :class="testingId === channel.id ? 'fas fa-spinner fa-spin' : 'fas fa-paper-plane'"></i>
                Test
              </button>
              <button class="btn btn-sm btn-secondary" @click="openAttemptsDrawer(channel)" title="View delivery history">
                <i class="fas fa-history"></i>
              </button>
              <button class="btn btn-sm btn-warning" @click="openEditModal(channel)" title="Edit channel">
                <i class="fas fa-edit"></i>
              </button>
              <button class="btn btn-sm btn-danger" @click="confirmDelete(channel)" title="Delete channel">
                <i class="fas fa-trash"></i>
              </button>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- Add/Edit Modal -->
    <div v-if="showModal" class="modal-overlay" @click.self="closeModal">
      <div class="modal">
        <div class="modal-header">
          <h3>{{ editingChannel ? 'Edit Channel' : 'Add Channel' }}</h3>
          <button class="btn-close" @click="closeModal">×</button>
        </div>
        <form @submit.prevent="saveChannel">
          <div class="modal-body">
            <div v-if="formErrors.length" class="alert alert-error">
              <ul>
                <li v-for="err in formErrors" :key="err.propertyPath">
                  <strong>{{ err.propertyPath }}</strong>: {{ err.message }}
                </li>
              </ul>
            </div>

            <div class="form-group">
              <label>Channel Type *</label>
              <select v-model="form.channelType" required :disabled="!!editingChannel" class="form-control">
                <option value="">Select type…</option>
                <option value="slack_webhook">Slack Webhook</option>
                <option value="generic_webhook">Generic Webhook</option>
              </select>
            </div>

            <div class="form-group">
              <label>Label *</label>
              <input v-model="form.label" type="text" class="form-control" placeholder="e.g. Ops Slack" required maxlength="255" />
            </div>

            <div class="form-group">
              <label>Webhook URL *</label>
              <input v-model="form.webhookUrl" type="url" class="form-control" placeholder="https://…" required />
            </div>

            <div class="form-group form-check">
              <label>
                <input v-model="form.enabled" type="checkbox" />
                Enabled
              </label>
            </div>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-secondary" @click="closeModal">Cancel</button>
            <button type="submit" class="btn btn-primary" :disabled="store.loading">
              <i v-if="store.loading" class="fas fa-spinner fa-spin"></i>
              {{ editingChannel ? 'Save Changes' : 'Create Channel' }}
            </button>
          </div>
        </form>
      </div>
    </div>

    <!-- Delivery Attempts Drawer -->
    <div v-if="attemptsChannel" class="modal-overlay" @click.self="closeAttemptsDrawer">
      <div class="modal modal-wide">
        <div class="modal-header">
          <h3>Delivery History — {{ attemptsChannel.label }}</h3>
          <button class="btn-close" @click="closeAttemptsDrawer">×</button>
        </div>
        <div class="modal-body">
          <div v-if="attemptsLoading" class="loading-state">
            <i class="fas fa-spinner fa-spin"></i> Loading…
          </div>
          <div v-else-if="!currentAttempts.length" class="empty-state">
            <p>No delivery attempts yet.</p>
          </div>
          <table v-else class="channels-table">
            <thead>
              <tr>
                <th>#</th>
                <th>Trigger</th>
                <th>Outcome</th>
                <th>HTTP</th>
                <th>Error</th>
                <th>Date</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="a in currentAttempts" :key="a.id">
                <td>{{ a.attemptNumber }}</td>
                <td>{{ a.triggerSource }}</td>
                <td>
                  <span :class="['badge', a.outcome === 'success' ? 'badge-enabled' : 'badge-disabled']">
                    {{ a.outcome }}
                  </span>
                </td>
                <td>{{ a.httpStatusCode ?? '—' }}</td>
                <td class="error-cell">{{ a.errorMessage ?? '—' }}</td>
                <td>{{ formatDate(a.attemptedAt) }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>

    <!-- Delete confirmation -->
    <div v-if="deletingChannel" class="modal-overlay" @click.self="deletingChannel = null">
      <div class="modal modal-sm">
        <div class="modal-header">
          <h3>Delete Channel</h3>
        </div>
        <div class="modal-body">
          <p>Delete <strong>{{ deletingChannel.label }}</strong>? This action cannot be undone.</p>
        </div>
        <div class="modal-footer">
          <button class="btn btn-secondary" @click="deletingChannel = null">Cancel</button>
          <button class="btn btn-danger" @click="doDelete" :disabled="store.loading">
            <i v-if="store.loading" class="fas fa-spinner fa-spin"></i> Delete
          </button>
        </div>
      </div>
    </div>

    <!-- Test result toast -->
    <div v-if="testResult" :class="['toast', testResult.success ? 'toast-success' : 'toast-error']">
      <i :class="testResult.success ? 'fas fa-check-circle' : 'fas fa-times-circle'"></i>
      {{ testResult.message }}
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { useNotificationChannelsStore } from '../stores/notificationChannels.js'

const store = useNotificationChannelsStore()

const showModal = ref(false)
const editingChannel = ref(null)
const deletingChannel = ref(null)
const attemptsChannel = ref(null)
const attemptsLoading = ref(false)
const testingId = ref(null)
const testResult = ref(null)
const formErrors = ref([])

const form = ref({
  channelType: '',
  label: '',
  webhookUrl: '',
  enabled: true,
})

const currentAttempts = computed(() => {
  if (!attemptsChannel.value) return []
  return store.deliveryAttempts[attemptsChannel.value.id] ?? []
})

onMounted(() => store.fetchChannels())

function openAddModal() {
  editingChannel.value = null
  form.value = { channelType: '', label: '', webhookUrl: '', enabled: true }
  formErrors.value = []
  showModal.value = true
}

function openEditModal(channel) {
  editingChannel.value = channel
  form.value = {
    channelType: channel.channelType,
    label: channel.label,
    webhookUrl: channel.webhookUrl,
    enabled: channel.enabled,
  }
  formErrors.value = []
  showModal.value = true
}

function closeModal() {
  showModal.value = false
  editingChannel.value = null
}

async function saveChannel() {
  formErrors.value = []
  let result
  if (editingChannel.value) {
    result = await store.updateChannel(editingChannel.value.id, form.value)
  } else {
    result = await store.createChannel(form.value)
  }
  if (result.success) {
    closeModal()
  } else if (result.violations) {
    formErrors.value = result.violations
  }
}

function confirmDelete(channel) {
  deletingChannel.value = channel
}

async function doDelete() {
  if (!deletingChannel.value) return
  const result = await store.deleteChannel(deletingChannel.value.id)
  if (result.success) deletingChannel.value = null
}

async function openAttemptsDrawer(channel) {
  attemptsChannel.value = channel
  attemptsLoading.value = true
  await store.fetchDeliveryAttempts(channel.id)
  attemptsLoading.value = false
}

function closeAttemptsDrawer() {
  attemptsChannel.value = null
}

async function sendTest(channel) {
  testingId.value = channel.id
  testResult.value = null
  const result = await store.sendTest(channel.id)
  testingId.value = null
  testResult.value = result
  setTimeout(() => { testResult.value = null }, 5000)
}

function formatType(type) {
  return type === 'slack_webhook' ? 'Slack' : 'Generic Webhook'
}

function formatDate(iso) {
  if (!iso) return '—'
  return new Date(iso).toLocaleString()
}
</script>

<style scoped>
.notification-channels-page { padding: 24px; }
.page-header { display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 24px; }
.header-content h1 { font-size: 1.5rem; font-weight: 600; margin: 0 0 4px; }
.header-content p { margin: 0; color: #6b7280; font-size: 0.9rem; }
.alert { padding: 12px 16px; border-radius: 6px; margin-bottom: 16px; display: flex; align-items: center; gap: 8px; }
.alert-error { background: #fee2e2; color: #991b1b; border: 1px solid #fca5a5; }
.loading-state, .empty-state { text-align: center; padding: 48px; color: #6b7280; }
.empty-state i { font-size: 3rem; margin-bottom: 16px; display: block; }
.channels-table-wrapper { overflow-x: auto; }
.channels-table { width: 100%; border-collapse: collapse; font-size: 0.9rem; }
.channels-table th, .channels-table td { padding: 10px 12px; text-align: left; border-bottom: 1px solid #e5e7eb; }
.channels-table th { background: #f9fafb; font-weight: 600; color: #374151; }
.actions-cell { display: flex; gap: 6px; align-items: center; }
.badge { display: inline-block; padding: 2px 8px; border-radius: 12px; font-size: 0.75rem; font-weight: 500; }
.badge-type { background: #e0e7ff; color: #3730a3; }
.badge-enabled { background: #d1fae5; color: #065f46; }
.badge-disabled { background: #f3f4f6; color: #6b7280; }
.badge-delivery-success { background: #d1fae5; color: #065f46; }
.badge-delivery-failed { background: #fee2e2; color: #991b1b; }
.badge-delivery-pending { background: #fef3c7; color: #92400e; }
.text-muted { color: #9ca3af; }
.btn { padding: 8px 16px; border: none; border-radius: 6px; cursor: pointer; font-size: 0.875rem; font-weight: 500; display: inline-flex; align-items: center; gap: 6px; }
.btn:disabled { opacity: 0.6; cursor: not-allowed; }
.btn-primary { background: #3b82f6; color: white; }
.btn-secondary { background: #e5e7eb; color: #374151; }
.btn-warning { background: #f59e0b; color: white; }
.btn-danger { background: #ef4444; color: white; }
.btn-info { background: #06b6d4; color: white; }
.btn-sm { padding: 4px 10px; font-size: 0.8rem; }
.btn-close { background: none; border: none; font-size: 1.2rem; cursor: pointer; color: #6b7280; margin-left: auto; }
.modal-overlay { position: fixed; inset: 0; background: rgba(0,0,0,0.4); display: flex; align-items: center; justify-content: center; z-index: 1000; }
.modal { /* neutralise le .modal de Bootstrap (display:none + position:fixed plein écran) */ display: block; position: relative; top: auto; left: auto; height: auto; z-index: auto; background: white; border-radius: 8px; width: 480px; max-width: 95vw; max-height: 90vh; overflow-y: auto; box-shadow: 0 20px 60px rgba(0,0,0,0.2); }
.modal-wide { width: 700px; }
.modal-sm { width: 360px; }
.modal-header { display: flex; align-items: center; justify-content: space-between; padding: 16px 20px; border-bottom: 1px solid #e5e7eb; }
.modal-header h3 { margin: 0; font-size: 1.1rem; }
.modal-body { padding: 20px; }
.modal-footer { padding: 16px 20px; border-top: 1px solid #e5e7eb; display: flex; justify-content: flex-end; gap: 10px; }
.form-group { margin-bottom: 16px; }
.form-group label { display: block; margin-bottom: 6px; font-weight: 500; font-size: 0.875rem; color: #374151; }
.form-control { width: 100%; padding: 8px 12px; border: 1px solid #d1d5db; border-radius: 6px; font-size: 0.875rem; box-sizing: border-box; }
.form-check label { display: flex; align-items: center; gap: 8px; font-weight: normal; }
.error-cell { max-width: 200px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.toast { position: fixed; bottom: 24px; right: 24px; padding: 12px 20px; border-radius: 8px; font-weight: 500; display: flex; align-items: center; gap: 8px; z-index: 2000; box-shadow: 0 4px 12px rgba(0,0,0,0.2); }
.toast-success { background: #d1fae5; color: #065f46; border: 1px solid #6ee7b7; }
.toast-error { background: #fee2e2; color: #991b1b; border: 1px solid #fca5a5; }
.header-content { min-width: 0; }
.alert { overflow-wrap: anywhere; }
.actions-cell { white-space: nowrap; }
.modal-body { overflow-x: auto; }
.modal-header h3 { min-width: 0; overflow-wrap: anywhere; }

@media (max-width: 991.98px) {
  .notification-channels-page { padding: 0; }
  .page-header { gap: 12px; }
}

@media (max-width: 767.98px) {
  .page-header { flex-direction: column; align-items: stretch; text-align: left; margin-bottom: 16px; }
  .header-actions .btn { width: 100%; justify-content: center; min-height: 44px; }
  .empty-state, .loading-state { padding: 32px 12px; }
  .channels-table { min-width: 640px; }
  .channels-table th, .channels-table td { padding: 8px 10px; }
  .modal-overlay { align-items: stretch; }
  .modal, .modal-wide, .modal-sm { width: 100%; max-width: 100%; max-height: none; height: 100%; border-radius: 0; display: flex; flex-direction: column; }
  .modal > form { display: flex; flex-direction: column; flex: 1; min-height: 0; }
  .modal-body { flex: 1; overflow-y: auto; }
  .modal-footer { flex-wrap: wrap; }
  .modal-footer .btn { flex: 1 1 auto; justify-content: center; min-height: 44px; }
  .toast { left: 12px; right: 12px; bottom: 12px; }
}

@media (pointer: coarse) {
  .actions-cell .btn-sm, .btn-close { min-width: 44px; min-height: 44px; justify-content: center; }
}
</style>
